/**
 * BottomNav — mobile-only bottom tab bar with Home / Search / Orders / Cart / You.
 *
 * Only renders on viewports ≤ 768px AND when the cart bar would otherwise
 * be hiding (i.e. the cart bar takes precedence — they don't stack).
 *
 * Tapping "Search" triggers a search-overlay open via a callback so the
 * header can host a single search instance.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Search, Package, ShoppingCart, User } from 'lucide-react';
import { useCartSummary } from '../../hooks/useCartSummary';
import { useAuth } from '../../hooks/useAuth';
import { css } from '../../design';

interface NavItem {
  key: string;
  label: string;
  to: string;
  icon: React.ReactNode;
  badge?: number;
  requiresAuth?: boolean;
}

interface BottomNavProps {
  onSearchClick?: () => void;
}

const BottomNav: React.FC<BottomNavProps> = ({ onSearchClick }) => {
  const location = useLocation();
  const { item_count } = useCartSummary();
  const { isAuthenticated, user } = useAuth();

  // Hide on auth pages / admin / dashboards.
  const hidden =
    /^\/(login|signup|register|admin|store-dashboard|deliveries|payment|order-confirmed)/.test(location.pathname);

  // Hide for non-customer roles — they have their own dashboards.
  const isCustomerLike = !isAuthenticated || user?.role === 'customer';
  if (hidden || !isCustomerLike) return null;

  const items: NavItem[] = [
    { key: 'home',   label: 'Home',    to: '/',          icon: <Home size={20} /> },
    { key: 'search', label: 'Search',  to: '#search',    icon: <Search size={20} /> },
    { key: 'orders', label: 'Orders',  to: '/orders',    icon: <Package size={20} />, requiresAuth: true },
    { key: 'cart',   label: 'Cart',    to: '/cart',      icon: <ShoppingCart size={20} />, badge: item_count, requiresAuth: true },
    { key: 'you',    label: 'You',     to: isAuthenticated ? '/profile' : '/login', icon: <User size={20} /> },
  ];

  const isActive = (it: NavItem) => {
    if (it.to === '/') return location.pathname === '/';
    if (it.to === '#search') return false;
    return location.pathname.startsWith(it.to);
  };

  return (
    <nav
      role="navigation"
      aria-label="Primary"
      className="pm-bottom-nav"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1020,
        background: css.color.surface,
        borderTop: `1px solid ${css.color.border}`,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        boxShadow: '0 -6px 16px rgba(15,23,42,0.06)',
      }}
    >
      <ul
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${items.length}, 1fr)`,
          margin: 0,
          padding: 0,
          listStyle: 'none',
          height: 64,
        }}
      >
        {items.map((it) => {
          const active = isActive(it);
          const inner = (
            <span
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                color: active ? css.color.primary : css.color.textMuted,
                width: '100%',
                height: '100%',
                fontSize: 11,
                fontWeight: active ? 700 : 600,
              }}
            >
              <span style={{ position: 'relative' }}>
                {it.icon}
                {it.badge != null && it.badge > 0 && (
                  <span
                    aria-hidden
                    style={{
                      position: 'absolute',
                      top: -6,
                      right: -10,
                      minWidth: 16,
                      height: 16,
                      padding: '0 4px',
                      borderRadius: 999,
                      background: css.color.danger,
                      color: '#FFFFFF',
                      fontSize: 10,
                      fontWeight: 800,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 0 0 2px var(--pm-color-surface)',
                    }}
                  >
                    {it.badge > 99 ? '99+' : it.badge}
                  </span>
                )}
              </span>
              <span>{it.label}</span>
            </span>
          );

          if (it.to === '#search') {
            return (
              <li key={it.key}>
                <button
                  type="button"
                  onClick={onSearchClick}
                  aria-label="Open search"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    width: '100%',
                    height: '100%',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  {inner}
                </button>
              </li>
            );
          }

          return (
            <li key={it.key}>
              <Link
                to={it.to}
                style={{ textDecoration: 'none', display: 'block', height: '100%' }}
                aria-current={active ? 'page' : undefined}
              >
                {inner}
              </Link>
            </li>
          );
        })}
      </ul>

      <style>{`
        /* Hide bottom nav on desktop — the navbar takes over. */
        @media (min-width: 768px) {
          .pm-bottom-nav { display: none !important; }
        }
      `}</style>
    </nav>
  );
};

export default BottomNav;

